import * as Sentry from '@sentry/react';
import { decode } from '@mapbox/polyline';
import { Location } from '../core/apiTypes';
import { MAP_DEFAULT_ZOOM, MAP_DEFAULT_CENTER } from './constants';

// import customGoogleMapStyle from '../resources/style/leCabMap';
// import customGoogleMapMobileStyle from '../resources/style/leCabMapMobile';
import customGoogleMapAxelStyle from '../resources/style/leCabMapAxel';
import customGoogleMapAxelDarkStyle from '../resources/style/leCabMapAxelDark';

export const mapUtils = {
    defaultZoom: MAP_DEFAULT_ZOOM,
    defaultCenter: MAP_DEFAULT_CENTER,
    addMarker: (maps: any, map: any, position: Location, title: string, label: string) => {
        if (maps) {
            const marker = new maps.Marker({
                position,
                map,
                title,
                label,
            });
            return marker;
        }
        return null;
    },
    removeMarker: (marker: any) => {
        if (marker) {
            marker.setMap(null);
        }
    },
};

function deg2rad(degrees) {
    const pi = Math.PI;
    return degrees * (pi / 180);
}

export function getLocationFromGoogle(coord: any): Location {
    return { lat: coord.lat() ?? coord.lat, lng: coord.lng() ?? coord.lng };
}

export function prepareWaypoints(route = ''): Location[] {
    if (!route) {
        return [];
    }
    const unformatedLocation = decode(route);
    return unformatedLocation?.map((el) => {
        return { lat: el[0], lng: el[1] };
    });
}

export function distanceBetween(coord1: Location, coord2: Location): number {
    if (!coord1 || !coord2) {
        return 0;
    }

    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(coord2.lat - coord1.lat);
    const dLon = deg2rad(coord2.lng - coord1.lng);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(coord1.lat)) * Math.cos(deg2rad(coord2.lat)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c; // Distance in km
    return d;
}

export function isValidLocation(coordinate: Location): boolean {
    return !!coordinate && Number.isFinite(coordinate.lat) && Number.isFinite(coordinate.lng);
}

export function getBounds(coordinates: Location[], maps: any): any {
    const bounds = new maps.LatLngBounds();
    // A coordinate without a usable lat/lng builds a NaN LatLng, which poisons the whole bounds —
    // see fitBounds below for what that costs us. Drop those instead of extending with them.
    (coordinates ?? []).forEach((coordinate) => {
        if (isValidLocation(coordinate)) {
            bounds.extend(new maps.LatLng(coordinate.lat, coordinate.lng));
        }
    });
    return bounds;
}

// Google Maps leaves the map without a centre when fitBounds() gets an empty or NaN bounds:
// map.getCenter() then returns undefined, and every later `idle` / `draw` / `zoom_changed` event
// throws inside google-map-react's _onBoundsChanged, which does map.getCenter().lat() unguarded.
// One bad call breaks the map for the rest of its life, far from the code that caused it — so
// swallow the bad call here rather than let it through.
export function fitBounds(map: any, bounds: any, style?: any): void {
    if (!map) {
        console.error('Map is ', map);
    } else if (!bounds || typeof bounds.isEmpty !== 'function' || bounds.isEmpty()) {
        const message = `Bounds are invalid: ${JSON.stringify(bounds?.toJSON?.() ?? bounds)}`;
        Sentry.captureMessage(message, Sentry.Severity.Debug);
        console.error(message);
    } else {
        map.fitBounds(bounds, style);
    }
}

export function getCustomMap(mode: 'dark' | 'light' = 'light'): any {
    // const isMobile = window.matchMedia('(max-width: 768px)').matches;
    // if (isMobile) {
    //     // The user is on a mobile device or a screen width smaller than 768px
    //     return customGoogleMapMobileStyle;
    // }
    // return customGoogleMapStyle;

    if (mode === 'dark') {
        return customGoogleMapAxelDarkStyle;
    }
    return customGoogleMapAxelStyle; // Share between web and mobile
}

export function setCenter(map: any, center: Location): void {
    if (!map) {
        console.error('Map is ', map);
    } else if (!center || typeof center.lat !== 'number' || typeof center.lng !== 'number') {
        const error = new Error('Center is invalid');
        const stackTrace = error.stack || 'No stack trace available';
        const message = `Center is invalid: ${JSON.stringify(center)} - Stack trace: ${stackTrace}`;
        Sentry.captureMessage(message, Sentry.Severity.Debug);
        console.error(message);
    } else {
        map.setCenter(center);
    }
}

// Compute route using google api
// export function computeRoute(map, maps): void {
//     const directionsService = new maps.DirectionsService();
//     const directionsRenderer = new maps.DirectionsRenderer();
//     directionsRenderer.setMap(map);
//     const origin = { lat: 48.879366, lng: 2.241271 };
//     const destination = { lat: 48.893671, lng: 2.256551 };
//     directionsService.route(
//         {
//             origin: origin,
//             destination: destination,
//             travelMode: maps.TravelMode.DRIVING,
//         },
//         (result, status) => {
//             if (status === maps.DirectionsStatus.OK) {
//                 console.log('result', result);
//                 directionsRenderer.setDirections(result);
//             } else {
//                 console.error(`error fetching directions ${result}`);
//             }
//         },
//     );
// };
